// Pure steering law for the Bolo3K virtual stick: translates a normalized
// joystick vector into a TankKey bitfield relative to the tank's current
// heading. No DOM, no state — touchControls.ts wraps this with the pointer
// machinery and calls it once per readKeys() frame.
//
// Direction encoding (verified against the sim):
// - `dir` is a fractional number in [0, 256): 0 = North (up), increasing
//   CLOCKWISE — 64 = East, 128 = South, 192 = West. See
//   packages/sim/src/math.ts:1-3 (Brain.h convention) and dirVector()
//   (dx = sin, dy = -cos with screen +y down).
// - TurnLeft DECREASES dir, TurnRight INCREASES it: movement.ts:133
//   `const delta = turnLeft ? -rate : rate`.
// - Screen/stick coords share the sim's handedness: +x right, +y down, so
//   the stick-to-heading conversion is atan2(x, -y).

import { TankKey } from '@bolo2k/sim';

export interface StickVector {
  /** Normalized horizontal deflection, +x = right, magnitude of (x,y) in 0..1. */
  x: number;
  /** Normalized vertical deflection, +y = DOWN (screen coords). */
  y: number;
}

export interface SteerOptions {
  /** Stick magnitude at or below which steer() emits nothing (and
   *  brakeBits() considers the stick released). Default 0.15. */
  deadzone?: number;
  /** Stick magnitude at or above which Faster may be emitted. Default 0.3. */
  thrustMagnitude?: number;
  /** |angular delta| in dir units (1/256 circle) at or below which the tank
   *  counts as aligned and no turn bit is emitted. Default 4 (~5.6°) —
   *  comfortably inside the sim's 16-step snapDir() grid so the sprite
   *  facing matches the commanded heading. */
  turnTolerance?: number;
  /** Faster is emitted only while |angular delta| is strictly under this
   *  many dir units. Default 40 (~56°): wide-enough corrections pivot in
   *  place before any forward creep — playtest feedback was that the
   *  original 90° cone made the tank drift out of cover while aiming. */
  thrustCone?: number;
  /** |angular delta| in dir units at or beyond which a held stick ENTERS
   *  the brake state while the tank is moving. Default 88 (~124° off
   *  heading — a ~112°-wide rear wedge): pulling back = stop, matching
   *  mobile driving-game intuition. Once stopped, the same hold becomes a
   *  turn-in-place U-turn. */
  brakeEnterCone?: number;
  /** Hysteresis: once braking, the state holds until |delta| drops BELOW
   *  this (or the stick releases / the tank stops). Default 64 (90°) —
   *  finger drift inside the rear half-plane never drops the brake;
   *  leaving it takes a deliberate push toward the tank's heading.
   *  Playtest feedback: a single cone was too twitchy to stay inside. */
  brakeExitCone?: number;
  /** Speed (wu/tick) at or below which the tank counts as stopped for the
   *  pull-back-brake rule. Default 0.5. */
  stopEpsilon?: number;
}

export const STEER_DEADZONE = 0.15;
/** Twin-ring intent: light deflection = aim (turn only), pushed to the rim
 *  = drive. Enter/exit thresholds differ (hysteresis) so thumb jitter at
 *  the ring boundary can't stutter the throttle. */
export const STEER_THRUST_MAGNITUDE = 0.65;
export const STEER_THRUST_EXIT_MAGNITUDE = 0.55;
export const STEER_TURN_TOLERANCE = 4;
export const STEER_THRUST_CONE = 40;
export const STEER_BRAKE_ENTER_CONE = 88;
export const STEER_BRAKE_EXIT_CONE = 64;
export const STEER_STOP_EPSILON = 0.5;

/** Desired heading (fractional dir in [0, 256)) for a stick deflection.
 *  Stick up (0,-1) → 0 (North); right (1,0) → 64; down (0,1) → 128;
 *  left (-1,0) → 192. */
export function stickToDir(stick: StickVector): number {
  // dirVector(dir) = (sin a, -cos a); invert: a = atan2(x, -y).
  const a = Math.atan2(stick.x, -stick.y);
  const dir = (a / (2 * Math.PI)) * 256;
  return ((dir % 256) + 256) % 256;
}

/** The sim only MOVES along 16 quantized headings (multiples of 16 —
 *  movement.ts uses dirVector(snapDir(dir))), so the stick's desired
 *  heading snaps to that grid before any delta math. Without this the tank
 *  settles at e.g. continuous dir 14 (within old tolerance of the raw
 *  target) but MOVES along snapped 16 — permanently 22.5° off what the
 *  thumb commanded, which is how tanks wedged diagonally at 1-tile
 *  chokepoints. turnTolerance (4) < half a snap step (8), so alignment to
 *  the snapped target guarantees snapDir lands exactly on it. */
export function snapToMovementDir(dir: number): number {
  return (Math.round(dir / 16) * 16) % 256;
}

/** Signed shortest angular delta from `fromDir` to `toDir`, in dir units,
 *  in the half-open range (-128, 128]. Positive = clockwise = TurnRight
 *  territory; negative = counterclockwise = TurnLeft. Exactly-opposite
 *  headings (|delta| = 128) resolve to +128 (turn right) deterministically. */
export function shortestDelta(fromDir: number, toDir: number): number {
  let d = (toDir - fromDir) % 256;
  d = ((d % 256) + 256) % 256; // [0, 256)
  if (d > 128) d -= 256; // (-128, 128]
  return d;
}

/** Translate stick state + current tank heading into TankKey bits
 *  (TurnLeft / TurnRight / Faster). Pure: braking on release is the
 *  caller's job via brakeBits() (it needs tank speed, which steer()
 *  deliberately doesn't take). */
export function steer(stick: StickVector, tankDir: number, opts?: SteerOptions): number {
  const deadzone = opts?.deadzone ?? STEER_DEADZONE;
  const thrustMagnitude = opts?.thrustMagnitude ?? STEER_THRUST_MAGNITUDE;
  const turnTolerance = opts?.turnTolerance ?? STEER_TURN_TOLERANCE;
  const thrustCone = opts?.thrustCone ?? STEER_THRUST_CONE;

  const mag = Math.hypot(stick.x, stick.y);
  if (mag <= deadzone) return 0;

  const desired = snapToMovementDir(stickToDir(stick));
  const delta = shortestDelta(tankDir, desired);

  let bits = 0;
  if (Math.abs(delta) > turnTolerance) {
    bits |= delta > 0 ? TankKey.TurnRight : TankKey.TurnLeft;
  }
  if (mag >= thrustMagnitude && Math.abs(delta) < thrustCone) {
    bits |= TankKey.Faster;
  }
  return bits;
}

/** Auto-brake companion to steer(): returns the Slower bit when the stick
 *  is released (magnitude at or below the deadzone) while the tank still
 *  has speed, else 0. Kept separate so steer() stays a pure function of
 *  stick + heading; the wrapper knows the tank's speed. */
export function brakeBits(stick: StickVector, tankSpeed: number, opts?: SteerOptions): number {
  const deadzone = opts?.deadzone ?? STEER_DEADZONE;
  const mag = Math.hypot(stick.x, stick.y);
  if (mag <= deadzone && tankSpeed > 0) return TankKey.Slower;
  return 0;
}

export interface DriveState {
  /** TankKey bitfield for this frame. */
  bits: number;
  /** True while the pull-back brake is engaged — feed back in as
   *  `wasBraking` next frame (hysteresis), and drive the BRAKE visual
   *  from it so the annunciator matches behavior exactly. */
  braking: boolean;
  /** True while throttle is engaged (stick past the drive ring) — feed
   *  back in as `wasThrusting` next frame (ring-boundary hysteresis). */
  thrusting: boolean;
}

/** The full drive law — what touchControls feeds readKeys(). Composes:
 *  1. Stick released while moving → brake (Slower).
 *  2. Stick held in the rear wedge while moving → brake ONLY (no turn
 *     bits — heading holds steady while stopping). STICKY: entry needs
 *     |delta| ≥ brakeEnterCone, but once engaged it holds until |delta|
 *     < brakeExitCone (or release / full stop), so finger drift can't
 *     drop the brake mid-stop.
 *  3. Otherwise → steer(): turn toward the stick, thrust once inside the
 *     (narrow) thrust cone. A pull-back held past the stop becomes a
 *     turn-in-place U-turn via this branch.
 *  Bolo tanks have no reverse gear, so "brake" is the strongest stop the
 *  sim offers. */
export function drive(
  stick: StickVector,
  tankDir: number,
  tankSpeed: number,
  wasBraking: boolean,
  opts?: SteerOptions,
  wasThrusting = false,
): DriveState {
  const deadzone = opts?.deadzone ?? STEER_DEADZONE;
  const enterCone = opts?.brakeEnterCone ?? STEER_BRAKE_ENTER_CONE;
  const exitCone = opts?.brakeExitCone ?? STEER_BRAKE_EXIT_CONE;
  const stopEpsilon = opts?.stopEpsilon ?? STEER_STOP_EPSILON;

  const mag = Math.hypot(stick.x, stick.y);
  if (mag <= deadzone) {
    return { bits: tankSpeed > 0 ? TankKey.Slower : 0, braking: false, thrusting: false };
  }
  const delta = Math.abs(shortestDelta(tankDir, snapToMovementDir(stickToDir(stick))));
  const braking = tankSpeed > stopEpsilon && delta >= (wasBraking ? exitCone : enterCone);
  if (braking) {
    return { bits: TankKey.Slower, braking: true, thrusting: false };
  }
  // Ring-boundary hysteresis: once driving, the throttle holds until the
  // stick clearly retreats into the aim ring.
  const thrustAt = wasThrusting
    ? (opts?.thrustMagnitude !== undefined ? opts.thrustMagnitude : STEER_THRUST_EXIT_MAGNITUDE)
    : (opts?.thrustMagnitude ?? STEER_THRUST_MAGNITUDE);
  const bits = steer(stick, tankDir, { ...opts, thrustMagnitude: thrustAt });
  return { bits, braking: false, thrusting: (bits & TankKey.Faster) !== 0 };
}

/** Stateless compatibility wrapper over drive() (no hysteresis). */
export function driveBits(
  stick: StickVector,
  tankDir: number,
  tankSpeed: number,
  opts?: SteerOptions,
): number {
  return drive(stick, tankDir, tankSpeed, false, opts).bits;
}
